import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  LayoutGrid,
  AlertTriangle,
  AlertCircle,
  Users,
  Tag,
  Shield,
  FileText,
  Sparkles,
  Loader2,
  RefreshCw,
  Info,
} from 'lucide-react';
import { sendChatMessage } from '../../../api/AI/chat';
import { getComparativeContext, ComparativeContextView } from '../../../api/PreformanceManagement/PerformanceSnapshot';
import type { Employee, ReviewData } from '../index';

interface ComparativeContextPanelProps {
  employee: Employee;
  reviewData: ReviewData;
}

// Color palette for distribution bars — cycles through these for each item in a category
const DISTRIBUTION_COLORS = [
  'bg-green-500',
  'bg-blue-500',
  'bg-orange-500',
  'bg-indigo-500',
  'bg-yellow-500',
  'bg-red-500',
  'bg-emerald-600',
  'bg-slate-500',
  'bg-amber-600',
  'bg-purple-500',
];

// Category-specific color schemes for better visual distinction
const CATEGORY_COLOR_MAP: Record<string, string[]> = {
  scope: ['bg-green-500', 'bg-blue-500', 'bg-orange-500'],
  growth: ['bg-indigo-300', 'bg-indigo-500', 'bg-indigo-700'],
  risk: ['bg-gray-400', 'bg-yellow-500', 'bg-red-500'],
  reward: ['bg-emerald-600', 'bg-slate-500', 'bg-amber-600'],
  score: ['bg-sky-400', 'bg-sky-600', 'bg-sky-800'],
};

const getBarColor = (categoryKey: string, index: number): string => {
  const categoryColors = CATEGORY_COLOR_MAP[categoryKey];
  if (categoryColors && index < categoryColors.length) {
    return categoryColors[index];
  }
  return DISTRIBUTION_COLORS[index % DISTRIBUTION_COLORS.length];
};

const ComparativeContextPanel = ({
  employee,
  reviewData,
}: ComparativeContextPanelProps) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<
    'distribution' | 'outliers' | 'role-comparison' | 'narrative'
  >('distribution');
  const [narrativeSummary, setNarrativeSummary] = useState('');
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  const [contextData, setContextData] = useState<ComparativeContextView | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadContext = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const period = reviewData.snapshot?.period;
        if (!period) throw new Error('No period available');
        const data = await getComparativeContext(period, employee.id);
        setContextData(data);
      } catch (err) {
        console.error('Failed to load comparative context:', err);
        setLoadError(t('performanceSnapshot.comparative.loadError'));
      } finally {
        setIsLoading(false);
      }
    };
    loadContext();
  }, [employee.id, reviewData.snapshot?.period, t]);

  const handleGenerateNarrative = async () => {
    setIsGeneratingNarrative(true);
    try {
      const distributions = contextData?.distributions ?? {};
      const distributionLines = Object.entries(distributions)
        .map(([key, items]) => `- ${key}: ${items.map((d) => `${d.decision} (${d.count})`).join(', ')}`)
        .join('\n');

      const prompt = `Analyze organizational decision patterns for the current review cycle:

${distributionLines || '(No distribution data available)'}

Pattern Breaks Identified: ${contextData?.patternBreaks?.length ?? 0} cases requiring calibration review

Provide a narrative summary focusing on:
1. Organizational decision trends observed this cycle
2. Notable pattern anomalies requiring attention
3. Areas of calibration concern

IMPORTANT: Use decision-focused language like "pattern observed", "decision trend", "contextual difference".
Avoid performance language like "high performer", "low performer", "top X%".
Do not label or score people. Focus on decision patterns and calibration needs.`;

      const result = await sendChatMessage({ message: prompt, ephemeral: true });

      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message);
      }

      if (result.payload?.message) {
        // Verify the response looks like real narrative content, not an error message
        const msg = result.payload.message;
        const isLikelyError =
          msg.length < 200 &&
          (msg.toLowerCase().includes('error') ||
            msg.toLowerCase().includes('try again'));
        if (isLikelyError) {
          throw new Error(msg);
        }
        setNarrativeSummary(msg);
      } else {
        throw new Error('No response received');
      }
    } catch (error) {
      console.error('Error generating narrative:', error);
      setNarrativeSummary(
        t('performanceSnapshot.comparative.narrativeFallback'),
      );
    } finally {
      setIsGeneratingNarrative(false);
    }
  };

  const getTotalDecisions = (category: string) => {
    return contextData?.distributions[category]?.reduce(
      (sum, item) => sum + item.count,
      0,
    ) ?? 0;
  };

  const viewModes = [
    { id: 'distribution' as const, label: t('performanceSnapshot.comparative.views.distribution') },
    { id: 'outliers' as const, label: t('performanceSnapshot.comparative.views.patternBreaks') },
    { id: 'role-comparison' as const, label: t('performanceSnapshot.comparative.views.roleSignals') },
    { id: 'narrative' as const, label: t('performanceSnapshot.comparative.views.narrativeSummary') },
  ];

  // Build categories from API data keys, with translated labels where available
  const distributionCategories = Object.keys(contextData?.distributions ?? {}).map((key) => ({
    key,
    label: t(`performanceSnapshot.comparative.categories.${key}`, key.charAt(0).toUpperCase() + key.slice(1)),
  }));

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">
              {t('performanceSnapshot.comparative.loading')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <AlertCircle size={32} className="text-destructive" />
            <span className="ml-3 text-destructive">{loadError}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground flex items-center space-x-2">
              <BarChart3 size={24} />
              <span>{t('performanceSnapshot.comparative.title')}</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('performanceSnapshot.comparative.subtitle')}
            </p>
          </div>
        </div>

        {/* View Mode Selector */}
        <div className="flex space-x-2">
          {viewModes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setViewMode(mode.id)}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                viewMode === mode.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* Insufficient Data Notice */}
      {contextData?.insufficientData && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Info size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-foreground mb-1">
                {t('performanceSnapshot.comparative.insufficientDataTitle')}
              </h4>
              <p className="text-sm text-muted-foreground">
                {t('performanceSnapshot.comparative.insufficientDataDescription', { count: contextData.snapshotCount })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Distribution View */}
      {viewMode === 'distribution' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
              <LayoutGrid size={18} />
              <span>{t('performanceSnapshot.comparative.distributionTitle')}</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-6">
              {t('performanceSnapshot.comparative.distributionDescription')}
            </p>

            {distributionCategories.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('performanceSnapshot.comparative.noDistributionData')}
              </p>
            )}

            {distributionCategories.map((cat) => {
              const total = getTotalDecisions(cat.key);
              const items = contextData?.distributions[cat.key] ?? [];
              return (
                <div key={cat.key} className="mb-6 last:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-foreground">{cat.label}</h5>
                    <span className="text-sm text-muted-foreground">
                      {total} {t('performanceSnapshot.comparative.total')}
                    </span>
                  </div>
                  <div className="flex h-12 rounded-lg overflow-hidden">
                    {items.map((item, idx) => (
                      <div
                        key={idx}
                        className={`${getBarColor(cat.key, idx)} flex items-center justify-center text-white text-sm font-medium`}
                        style={{
                          width: total > 0 ? `${(item.count / total) * 100}%` : '0%',
                        }}
                      >
                        {item.count > 5 && `${item.decision} (${item.count})`}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    {items.map((item, idx) => (
                      <span key={idx}>{item.decision}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Info size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t('performanceSnapshot.comparative.decisionTrendsTitle')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('performanceSnapshot.comparative.decisionTrendsDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pattern Breaks View */}
      {viewMode === 'outliers' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
              <AlertTriangle size={18} className="text-amber-500" />
              <span>{t('performanceSnapshot.comparative.patternBreaksTitle')}</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-6">
              {t('performanceSnapshot.comparative.patternBreaksDescription')}
            </p>

            {(!contextData?.patternBreaks || contextData.patternBreaks.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('performanceSnapshot.comparative.noPatternBreaks')}
              </p>
            )}

            <div className="space-y-4">
              {contextData?.patternBreaks?.map((item) => (
                <div
                  key={item.snapshotId}
                  className="border border-border rounded-lg p-5 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-semibold text-foreground">
                          {item.role}
                        </span>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs font-medium rounded-full">
                          {item.decision}
                        </span>
                      </div>
                      <div className="flex items-start space-x-2 mb-2">
                        <AlertCircle
                          size={16}
                          className="text-amber-500 flex-shrink-0 mt-0.5"
                        />
                        <p className="text-sm font-medium text-foreground">
                          {item.patternContradiction}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground ml-6">
                        {item.details}
                      </p>
                    </div>
                    <button className="ml-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                      {t('performanceSnapshot.comparative.review')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Info size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t('performanceSnapshot.comparative.patternAnalysisPurpose')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('performanceSnapshot.comparative.patternAnalysisDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Comparison View */}
      {viewMode === 'role-comparison' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Users size={18} />
              <span>{t('performanceSnapshot.comparative.roleComparisonTitle')}</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-6">
              {t('performanceSnapshot.comparative.roleComparisonDescription')}
            </p>

            <div className="bg-muted/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-semibold text-foreground">
                    {employee.position || t('performanceSnapshot.comparative.unknownRole')}
                  </h5>
                </div>
              </div>
            </div>

            {(!contextData?.roleSignals || contextData.roleSignals.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('performanceSnapshot.comparative.noRoleSignals')}
              </p>
            )}

            <div className="space-y-4">
              {contextData?.roleSignals?.map((signal, idx) => (
                <div key={idx} className="border border-border rounded-lg p-5">
                  <h5 className="font-medium text-foreground mb-3 flex items-center space-x-2">
                    <Tag size={16} />
                    <span>{signal.category}</span>
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                      <div className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase mb-2">
                        {t('performanceSnapshot.comparative.employeeSignal')}
                      </div>
                      <div className="text-sm font-medium text-foreground">
                        {signal.employeeSignal}
                      </div>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase mb-2">
                        {t('performanceSnapshot.comparative.peerPattern')}
                      </div>
                      <div className="text-sm font-medium text-foreground">
                        {signal.peerPattern}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Shield size={20} className="text-purple-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t('performanceSnapshot.comparative.signalBasedTitle')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('performanceSnapshot.comparative.signalBasedDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Narrative Summary View */}
      {viewMode === 'narrative' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
              <FileText size={18} />
              <span>{t('performanceSnapshot.comparative.narrativeTitle')}</span>
            </h4>
            <p className="text-sm text-muted-foreground mb-6">
              {t('performanceSnapshot.comparative.narrativeDescription')}
            </p>

            {!narrativeSummary && (
              <div className="text-center py-12">
                <Sparkles size={48} className="text-purple-500 mx-auto mb-4" />
                <p className="text-muted-foreground mb-6">
                  {t('performanceSnapshot.comparative.generatePrompt')}
                </p>
                <button
                  onClick={handleGenerateNarrative}
                  disabled={isGeneratingNarrative}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
                >
                  {isGeneratingNarrative ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>{t('performanceSnapshot.comparative.generatingBtn')}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      <span>{t('performanceSnapshot.comparative.generateBtn')}</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {narrativeSummary && (
              <div>
                <div className="bg-muted/30 rounded-lg p-6 mb-4">
                  <div className="prose prose-sm max-w-none">
                    <div className="whitespace-pre-wrap text-foreground">
                      {narrativeSummary}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleGenerateNarrative}
                  disabled={isGeneratingNarrative}
                  className="px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isGeneratingNarrative ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>{t('performanceSnapshot.comparative.regeneratingBtn')}</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      <span>{t('performanceSnapshot.comparative.regenerateBtn')}</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle
                size={20}
                className="text-amber-500 flex-shrink-0 mt-0.5"
              />
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t('performanceSnapshot.comparative.aiGuidelinesTitle')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('performanceSnapshot.comparative.aiGuidelinesDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overall Disclaimer */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Shield size={20} className="text-purple-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">
              {t('performanceSnapshot.comparative.disclaimerTitle')}
            </h4>
            <p className="text-sm text-muted-foreground">
              {t('performanceSnapshot.comparative.disclaimerDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparativeContextPanel;
